import { Injectable, EventEmitter} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Loyaltic } from './api-url.constants';
import { Observable, Subject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface ReportChartConfig {
  showTotalUsers: boolean;
  showMarketingOptIns: boolean;
  showEmailOptIns: boolean;
  showTotalCodes: boolean;
  showTotalRewards: boolean;
  showParticipations: boolean;
  showOptInsChart: boolean;
  showCodeEntriesChart: boolean;
  showRewardsChart: boolean;
  showParticipationsChart: boolean;
  showSizeDistributionChart: boolean;
  showNotificationOptInsChart: boolean;
  showRegistrationTrend: boolean;
  showReceiptTrend: boolean;
  showRewardTrend: boolean;
  showProductTrend: boolean;
  totalUsersLabel: string;
  marketingOptInsLabel: string;
  emailOptInsLabel: string;
  totalCodesLabel: string;
  totalRewardsLabel: string;
  participationsLabel: string;
}

// NOTE: camelCase because ASP.NET Core's default System.Text.Json serializer camelCases
// declared C# property names. Row values keep whatever casing the SQL column alias used
// (e.g. "Vendor", "ReceiptCount") since they come through as a Dictionary<string, object>,
// which passes keys through verbatim rather than applying the naming policy.
export interface ChartSuggestion {
  chartType: 'line' | 'bar' | 'stat' | 'table';
  xAxisColumn: string | null;
  yAxisColumn: string | null;
  title: string | null;
}

export interface ReceiptReportResult {
  success: boolean;
  errorMessage?: string;
  generatedSql: string;
  simpleExplanation?: string;
  technicalExplanation?: string;
  columns: string[];
  rows: { [key: string]: any }[];
  chart: ChartSuggestion | null;
}

export interface ReceiptColumnDiagnosticInfo {
  table: string;
  column: string;
  total: number;
  populated: number;
  parseRate: number;
  sampleBadValues: string[];
}

export interface ReceiptSqlCritique {
  verdict: string; // 'Trustworthy' | 'Questionable' | 'Likely Wrong'
  reason: string;
  diagnostics: ReceiptColumnDiagnosticInfo[];
}

export interface ReceiptReportTableInfo {
  name: string;
  description: string;
  enabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CoreService {
  public pointsEmitter = new EventEmitter<any>();
  public responseLoadedEmitter = new EventEmitter<boolean>();
  isReceiptApproved = false;
  recptPartitionKey = '';
  failedChecks = 0;
  recptRowKey = '';
  scanningReasons:any[] = [];
  public rejectedReasons = [];
  
  constructor(private http: HttpClient) { }



  signUp(userName) {
    return this.http.post(environment.baseUrl + Loyaltic.API.CaptureEmail, JSON.stringify({ Email: userName }))
      .toPromise().then(response => {
        return response;
      });
  }

  getLiveConfig() {
    return this.http.get(environment.baseUrl + Loyaltic.API.LiveConfig)
      .toPromise().then(response => {
        return response;
      });
  }

  logIn(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Login, JSON.stringify(body))
      .toPromise().then(response => {
        return response;
      });
  }

  upLoadReceipt(payload) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Upload,
      payload)
      .toPromise().then(response => {
        return response;
      });
  }

  participate(payload) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Participate,
      payload)
      .toPromise().then(response => {
        return response;
      });
  }

  RegUploadWithRealTimeScanning(payload) {
    return this.http.post(environment.baseUrl + Loyaltic.API.RegUploadWithRealTimeScanning,
      payload)
      .toPromise().then(response => {
        return response;
      });
  }

  loginWithUploadReceipt(payload) {
    return this.http.post(environment.baseUrl + Loyaltic.API.LoginWithUploadReceipt,
      payload)
      .toPromise().then(response => {
        return response;
      });
  }

  getRegistrationTrends(startDate?: string, endDate?: string, period: number = 0): Observable<any> {
    let url = environment.baseUrl + Loyaltic.API.RegistrationTrends + `?period=${period}`;
    if (startDate) {
      url += `&startDate=${startDate}`;
    }
    if (endDate) {
      url += `&endDate=${endDate}`;
    }
    return this.http.get(url);
  }

  getReceiptTrends(startDate?: string, endDate?: string, period: number = 0): Observable<any> {
    let url = environment.baseUrl + Loyaltic.API.ReceiptTrends + `?period=${period}`;
    if (startDate) {
      url += `&startDate=${startDate}`;
    }
    if (endDate) {
      url += `&endDate=${endDate}`;
    }
    return this.http.get(url);
  }

  getRewardTrends(startDate?: string, endDate?: string, period: number = 0): Observable<any> {
    let url = environment.baseUrl + Loyaltic.API.RewardTrends + `?period=${period}`;
    if (startDate) {
      url += `&startDate=${startDate}`;
    }
    if (endDate) {
      url += `&endDate=${endDate}`;
    }
    return this.http.get(url);
  }

  getProductTrends(startDate?: string, endDate?: string): Observable<any> {
    let url = environment.baseUrl + Loyaltic.API.ProductTrends;
    const params: string[] = [];
    if (startDate) {
      params.push(`startDate=${startDate}`);
    }
    if (endDate) {
      params.push(`endDate=${endDate}`);
    }
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    return this.http.get(url);
  }

  getRewardSizeDistribution(): Observable<any> {
    return this.http.get(environment.baseUrl + Loyaltic.API.RewardSizeDistribution);
  }

  getRewardNotificationOptIns(): Observable<any> {
    return this.http.get(environment.baseUrl + Loyaltic.API.RewardNotificationOptIns);
  }

  fetchReport(type: any, startDate?: string, endDate?: string) {
    let url = environment.baseUrl + `${Loyaltic.API.FetchTotal}/${type}`;
    const params: string[] = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length > 0) url += '?' + params.join('&');
    return this.http.get(url);
  }

  getReportChartConfig(): Observable<ReportChartConfig> {
    return this.http.get<ReportChartConfig>(environment.baseUrl + Loyaltic.API.ReportChartConfig);
  }

  updateReportChartConfig(config: ReportChartConfig): Observable<ReportChartConfig> {
    return this.http.put<ReportChartConfig>(environment.baseUrl + Loyaltic.API.ReportChartConfig, config);
  }

  queryReceiptReport(question: string): Observable<ReceiptReportResult> {
    return this.http.post<ReceiptReportResult>(environment.baseUrl + Loyaltic.API.ReceiptsQuery, { question });
  }

  investigateReceiptReport(question: string, sql: string): Observable<ReceiptSqlCritique> {
    return this.http.post<ReceiptSqlCritique>(environment.baseUrl + Loyaltic.API.ReceiptsInvestigate, { question, sql });
  }

  getReceiptReportTableConfig(): Observable<{ tables: ReceiptReportTableInfo[] }> {
    return this.http.get<{ tables: ReceiptReportTableInfo[] }>(environment.baseUrl + Loyaltic.API.ReceiptsTableConfig);
  }

  updateReceiptReportTableConfig(enabledTables: string[]): Observable<any> {
    return this.http.put(environment.baseUrl + Loyaltic.API.ReceiptsTableConfig, { enabledTables });
  }


  getAvailableGames(userId) {
    return this.http.get(environment.baseUrl + Loyaltic.API.AvailableGames + '?userid=' + userId).toPromise().then(response => {
      return response;
    });
  }

  pay(body:any) {
    return this.http.post(environment.baseUrl + Loyaltic.API.pay, JSON.stringify(body)).toPromise().then(response => {
      return response;
    });
  }

  play(userId, playType) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Play, JSON.stringify({ UserId: userId, PlaySource: playType }))
      .toPromise().then(response => {
        return response;
      });
  }

  getfbBonuspoint(body, userId) {
    return this.http.post(environment.baseUrl + Loyaltic.API.FbSharePoints + '?userid=' + userId, JSON.stringify(body)).toPromise().then(response => {
      return response;
    });
  }

   testwin(rewardid) {
    return this.http.get(environment.baseUrl + Loyaltic.API.TestWin + '?reward=' + rewardid)
      .toPromise().then(response => {
        return response;
      });
  }



  captureAddress(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.CaptureAddress, JSON.stringify(body))
      .toPromise().then(response => {
        return response;
      });
  }

  captureDigitalWin(payload) {
    return this.http.post(environment.baseUrl + Loyaltic.API.CaptureDigitalWin , payload)
      .toPromise().then(response => {
        return response;
      });
  }

  stripePay(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.StripePay, JSON.stringify(body))
      .toPromise().then(response => {
        return response;
      });
  }

  confirmReceiptUpload(partitionKey, rowKey) {
    return this.http.post(environment.baseUrl +
      Loyaltic.API.confirmuploadafterscanning + '?PartitionKey=' + partitionKey + '&RowKey=' + rowKey, null)
      .toPromise().then(response => {
        return response;
      });
  }

  Notifyuser(userid, RewardPartitionkey) {
    return this.http.post(environment.baseUrl +
      Loyaltic.API.Notifyuser + '?userId=' + userid + '&RewardPartitionkey=' + RewardPartitionkey, null)
      .toPromise().then(response => {
        return response;
      });
  }

  updateRewardNotification(userid, RewardPartitionkey, isUserNotified) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Notifyuser, JSON.stringify({
      userId: userid,
      RewardPartitionkey,
      isUserNotified
    }))
      .toPromise().then(response => {
        return response;
      });
  }

  emailReminder(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Emailreminder, JSON.stringify(body)).toPromise().then(response => {
      return response;
    });
  }

  register(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.Register, JSON.stringify(body)).toPromise().then(response => {
      return response;
    });
  }

  forgotPassword(email) {
    return this.http.post(environment.baseUrl + Loyaltic.API.ForgotPassword, JSON.stringify({ Email: email }))
      .toPromise().then(response => {
        return response;
      });
  }

  resetPassword(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.ResetPassword, JSON.stringify(body))
      .toPromise().then(response => {
        return response;
      });
  }

  getLatestAddress(id) {
    return this.http.get(environment.baseUrl + Loyaltic.API.LatestAddress + '?userId=' + id).toPromise().then(response => {
      return response;
    });
  }

  postOrderReward(body) {
    return this.http.post(environment.baseUrl + Loyaltic.API.OrderReward, JSON.stringify(body)).toPromise().then(response => {
      return response;
    });
  }

  getRewards(culture, userId = null) {
    let query = `?culture=${culture}`;
    if (userId) {
      query = `${query}&userid=${userId}`;
    }
    return this.http.get(environment.baseUrl + Loyaltic.API.Rewards + query).toPromise().then(response => {
      return response;
    });
  }


  getprofile(userId) {  
    return this.http.get(environment.baseUrl + Loyaltic.API.profile + '?userId=' + userId).toPromise().then(response => {
      return response;
    });
  }

  getPoints(userId) {
    return this.http.get(environment.baseUrl + Loyaltic.API.Points + '?userid=' + userId)
      .toPromise().then(response => {
        return response;
      });
  }

  getProducts(){
    return this.http.get(environment.baseUrl + Loyaltic.API.products).toPromise().then(response => {
      return response;
    })
  }

}
